import {autoinject, BindingEngine, computedFrom, useView} from "aurelia-framework";
import { SharedActivitiesService } from "../../../../../../services/modules/shared-activities-client.service";
import { SharedActivityModel } from "../../../../../../models/customers/shared-activities.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { CustomGridPageBase } from "../../../../common/custom-grid-page.base";
import { I18N } from "aurelia-i18n";
import { SourceType, DataType, QueryFilter } from "../../../../../../models/queries/queries.models";
import { MenuItem } from "../../../../../components/menu/menu-item";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { LookupValue } from "../../../../../../models/lookups/lookup.models";
import { EntityTypeConfigurationHelper, EntityTypeConfigurationModel } from "../../../../../../models/basics/basics.models";
import { LookupClientService } from "../../../../../../services/lookups/lookup-client.service";
import { Reflection } from "../../../../../../utilities/reflection";

@autoinject()
@useView('views/pages/modules/customers/steps/shared-activity/shared-activities.html')
export class SharedActivities extends CustomGridPageBase {

    public customerEntityTypes: EntityTypeConfigurationModel[];
    public businessUnitEntityTypes: EntityTypeConfigurationModel[];
    public customerId: number; 
    public isMobileFilterOpen: boolean;
    public showActivityFilter: boolean;
    public showBUFilter: boolean;
    public showCustomerFilter: boolean;
    public showWithBUFilter: boolean;
    public selectedActivites: number[] = [];
    public selectedBUs: number[] = [];
    public selectedCustomers: number[] = [];
    public selectedWithBUs: number[] = [];
    public activities: LookupValue[] = [];
    public bagCustomerIdString: string;
    public BUs: any[] = [];
    filters: QueryFilter[] = [];

    public selectedFilter: { title: string, clear: () => void, apply: () => void };

    constructor(private sharedActivitiesService: SharedActivitiesService,
        private dialogClientService: DialogClientService, i18n: I18N, protected bindingEngine: BindingEngine, 
        private notificationClientService: NotificationClientService, private entityTypeConfigurationHelper: EntityTypeConfigurationHelper,
        protected lookupsClientService: LookupClientService)  {
            super(i18n, 'Shared Activities', bindingEngine);
            this.customerEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Customers"]);
            this.businessUnitEntityTypes= this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["BusinessUnits"]);
    }
    
    @computedFrom("selectedActivites", "selectedActivites.length")
    public get activityFilterActive() {
        return this.selectedActivites && this.selectedActivites.length > 0;
    }
    
    @computedFrom("selectedBUs", "selectedBUs.length")
    public get BUFilterActive() {
        return this.selectedBUs && this.selectedBUs.length > 0;
    }

    @computedFrom("selectedCustomers", "selectedCustomers.length")
    public get customerFilterActive() {
        return this.selectedCustomers && this.selectedCustomers.length > 0;
    }

    @computedFrom("selectedWithBUs", "selectedWithBUs.length")
    public get WithBUFilterActive() {
        return this.selectedWithBUs && this.selectedWithBUs.length > 0;
    }

    @computedFrom("selectedCustomers[0]")
    public get bagSelectedCustomerBUs() {
        return '{ customerId : ' + this.selectedCustomers[0]+ '}';
    }
    
    activate(params) {
        this.customerId = params.customerId;
        this.bagCustomerIdString = '{ customerId : '+ this.customerId.toString() + '}';
        this.activities = [
            <LookupValue>{ id: 4, name: 'Vet' },
            <LookupValue>{ id: 18, name: 'GHG Verification' },
            <LookupValue>{ id: 16, name: 'FBR' },
            <LookupValue>{ id: 6, name: 'Forms' }
        ]
        this.columns = [                
            {
                sortOrder: 1,
                columnOrder: 1,
                name: "entityType",
                dataType: DataType.Default,
                title: 'Activity',
                isFilterable: false
            },
            {
                sortOrder: 2,
                columnOrder: 2,
                name: "sharedByBusinessUnit",
                dataType: DataType.Default,
                title: 'By business unit',
                isFilterable: false
            },
            {
                sortOrder: 3,
                columnOrder: 3,
                name: "sharedWithCustomer",
                dataType: DataType.Default,
                title: 'With customer',
                isFilterable: false
            },
            {
                sortOrder: 4,
                columnOrder: 4,
                name: "sharedWithBusinessUnit",
                dataType: DataType.Default,
                title: 'With business unit',
                isFilterable: false
            },
            {
                sortOrder: 5,
                columnOrder: 5,
                name: "isActive",
                dataType: DataType.Boolean,
                title: 'Is Active?',
                isFilterable: false
            },
            {
                columnOrder: 99,
                sortOrder: 99,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: "menu",
                value: `<div class="actions row justify-content-end align-items-middle">
                            <list-menu hide-menu-border="true" menu-items.bind="menuItems(item)" show.bind="isVisible">
                                <template replace-part="menu-item">
                                    <div class="row nowrap align-items-middle" click.trigger="menuItem.click($event, item)">
                                        <span class="text">\${menuItem.item.text}</span>
                                    </div>  
                                </template>
                            </list-menu>
                        </div>`,
                parameter: {
                    item: null,
                    isVisible: true,//this.canManageRoles || this.canUpdatePermissions,
                    menuItems: (item) => 
                    {
                        const menuItems: MenuItem[] = [];
                
                        menuItems.push({isVisible: true, click: () => this.delete(item), item: { text: "Delete" }, classList: ''});
                        menuItems.push({isVisible: true, click: () => this.add(item), item: { text: "Edit" }, classList: ''});
                        return menuItems;
                    }
                }
            }
        ];

        this.setItemSource();
    }

    delete(sharedActivityModel: SharedActivityModel) {

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.DeleteSharedActivity, {
            onDelete: () => {                      

                return this.sharedActivitiesService.deleteActivity(sharedActivityModel.sharedActivityId, this.customerId).then(() => {
                    this.setItemSource();
                    this.dialogClientService.hide();
                });
            },
            onCancel: () => {
                this.dialogClientService.hide();
            }
        }, true, false, false);    
    }

    add(sharedActivityModel: SharedActivityModel = null) {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddSharedActivity, {
            customerId: this.customerId,
            onAdd: (request: { sharedActivityId?: number, byCustomerId: number, byBusinessUnitId: number, withCustomerId: number, withBusinessUnitId: number, EntityTypeId: number }) => {   
                
                return this.sharedActivitiesService.addActivity(request).then(() => {
                    this.setItemSource();                                         
                    this.dialogClientService.hide();
                    this.notificationClientService.show("Successfully " + (request.sharedActivityId ? "edited" : "added"), "", NotificationType.Success);
                }).catch(() => {
                    this.notificationClientService.show("Error while trying to add", "", NotificationType.Error);
                });
            },
            onCancel: () => {
                this.dialogClientService.hide();
            },
            sharedActivityModel: sharedActivityModel
        }, true, false, false, 'right');
    }

    toggleActivityFilters() {
        this.showActivityFilter = !this.showActivityFilter;
        this.selectedFilter = {
            title: "Activity",
            clear: () => {
                this.clearActivityFilters();
            },
            apply: () => {
                this.applyActivityFilters(this.selectedActivites.map(bId => {
                    return { id: bId };
                }), true);
            }
        }
    }

    clearActivityFilters() {
        this.selectedActivites = [];
        this.setItemSource();
    }

    applyActivityFilters(items: any[], reload: boolean = true) {
        this.selectedActivites = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleActivityFilters();
    }

    toggleBuFilters() {
        this.showBUFilter = !this.showBUFilter;
        this.selectedFilter = {
            title: "By Business Unit",
            clear: () => {
                this.clearBUFilter();
            },
            apply: () => {
                this.applyBuFilter(this.selectedBUs.map(bId => {
                    return { id: bId };
                }), true);
            }
        }
    }

    clearBUFilter () {
        this.selectedBUs = [];
        this.setItemSource();
    }

    applyBuFilter (items: any[], reload: boolean = true) {
        this.selectedBUs = items.map(x=> x.id);
        if (reload == true) this.setItemSource();
        this.toggleBuFilters();
    }

    toggleCustomerFilters() {
        this.showCustomerFilter = !this.showCustomerFilter;
        this.selectedFilter = {
            title: "With Customer",
            clear: () => {
                this.clearCustomerFilter();
            },
            apply: () => {
                this.applyCustomerFilter(this.selectedCustomers.map(bId => {
                    return { id: bId };
                }), true);
            }
        };
    }

    clearCustomerFilter () {
        this.selectedCustomers = [];
        this.setItemSource();
    }

    applyCustomerFilter (items: any[], reload: boolean = true) {
        this.selectedCustomers = items.map(x=> x.id);
        if (reload == true) this.setItemSource();
        this.toggleCustomerFilters();
    }

    toggleWithBuFilters() {
        this.showWithBUFilter = !this.showWithBUFilter;
        this.selectedFilter = {
            title: "With Business Units",
            clear: () => {
                this.clearCustomerFilter();
            },
            apply: () => {
                this.applyCustomerFilter(this.selectedCustomers.map(bId => {
                    return { id: bId };
                }), true);
            }
        };
    }

    clearWithBUFilter () {
        this.selectedWithBUs = [];
        this.setItemSource();
    }

    applyWithBuFilter (items: any[], reload: boolean = true) {
        this.selectedWithBUs = items.map(x=> x.id);
        if (reload == true) this.setItemSource();
        this.toggleWithBuFilters();
    }

    setItemSource() {
        const currentFilters: QueryFilter[] = [];        
        currentFilters.push(QueryFilter.getQuery("customerId", this.customerId, 'eq', DataType.Number));

        if (this.activityFilterActive) {

            const activityFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "entityTypeId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedActivites || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            currentFilters.push(activityFilter);
        }

        if (this.BUFilterActive) {
            const customerFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "sharedByBusinessUnitId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedBUs || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };
            currentFilters.push(customerFilter)
        }
        
        if (this.customerFilterActive) {

            const customerFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "sharedWithCustomerId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedCustomers || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };
            currentFilters.push(customerFilter)
        }

        if (this.WithBUFilterActive) {

            const WithBUFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "sharedWithBusinessUnitId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedWithBUs || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };
            currentFilters.push(WithBUFilter)
        }
        this.itemSource = this.getItemSource(currentFilters);
    }

    private getItemSource(filters) {
        this.filters = filters;
        return {
            sourceType: SourceType.ODataUrl,
            source: "odata/SharedActivities",
            columns: this.columns,
            initialSorting: this.initialSorting,
            initialFilters: this.filters
        };
    }

    clearAllFilters(){
        this.selectedActivites = [];
        this.selectedBUs = [];
        this.selectedCustomers = [];
        this.selectedWithBUs = [];
        this.setItemSource();
    }

    @computedFrom(Reflection.getPropertyPath<SharedActivities>(z => z.showActivityFilter),
        Reflection.getPropertyPath<SharedActivities>(z => z.showBUFilter),
        Reflection.getPropertyPath<SharedActivities>(z => z.showCustomerFilter),
        Reflection.getPropertyPath<SharedActivities>(z => z.showWithBUFilter))
    public get isSubFilterOpen(): boolean {
        return this.showActivityFilter || this.showBUFilter || this.showCustomerFilter || this.showWithBUFilter;
    }
    
    toggleMobileFilterDialog() {
        if(!this.isSubFilterOpen) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }
       this.showActivityFilter = false;
       this.showBUFilter = false;
       this.showCustomerFilter = false;
       this.showWithBUFilter = false; 
    }

    public mobileFilterApply(toggleDialog: boolean = true) {
        if (toggleDialog)
        this.toggleMobileFilterDialog();
    }

    public mobileApplySelectedFilter() {
        if (this.selectedFilter) {
            this.selectedFilter.apply();
            this.selectedFilter = null;
        }
    }
}